import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "@phosphor-icons/react";
import { Hero3D } from "@/components/Hero3D";
import { ChoosePathHub } from "@/components/ChoosePathHub";
import { ScrollBuildSequence } from "@/components/ScrollBuildSequence";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { StatCounter } from "@/components/StatCounter";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CTASection } from "@/components/CTASection";
import { useConcierge } from "@/context/ConciergeContext";
import { useI18n } from "@/context/I18nContext";
import { STATS, PROCESS, PROJECTS, FAQS, IMAGES, NAP } from "@/data/site";
import { track } from "@/lib/api";
import {
  useSEO,
  localBusinessLd,
  organizationLd,
  websiteLd,
  faqLd,
  breadcrumbLd,
} from "@/lib/seo";

export default function Home() {
  const { openConcierge } = useConcierge();
  const { t } = useI18n();

  useSEO({
    title: "MRE Developments | Build & Grow in Calgary — Free Quote",
    description:
      "Custom homes, renovations & commercial builds plus branding, web, marketing and AI — one partner for the structure and the signal. Get a free quote.",
    path: "/",
    jsonLd: [
      organizationLd(),
      localBusinessLd(),
      websiteLd(),
      faqLd(FAQS),
      breadcrumbLd([{ name: "Home", path: "/" }]),
    ],
  });

  const featured = PROJECTS.slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden border-b border-border" data-testid="hero">
        <Hero3D />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-transparent to-background" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 w-full pt-24 pb-16">
          <Reveal className="font-mono-tech text-xs sm:text-sm tracking-widest uppercase text-primary mb-6 flex items-center gap-3">
            <span className="inline-block w-10 h-px bg-primary" /> {t("hero.badge")}
          </Reveal>
          <Reveal as="h1" delay={80} className="font-display uppercase tracking-tight text-5xl sm:text-7xl lg:text-8xl leading-[0.86] max-w-4xl text-balance">
            {t("hero.l1")} <span className="text-primary">{t("hero.a1")}</span> {t("hero.l2")} <span className="text-primary">{t("hero.a2")}</span>
          </Reveal>
          <Reveal delay={160} className="mt-7 max-w-xl text-muted-foreground text-base sm:text-lg leading-relaxed">
            {t("hero.sub")}
          </Reveal>
          <Reveal delay={240} className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              onClick={() => track("cta_click", { cta: "free_quote", location: "hero" })}
              data-testid="hero-cta-button"
              className="group inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground font-mono-tech text-xs tracking-widest uppercase px-8 h-14 hover:bg-[hsl(16,100%,55%)] transition-colors"
            >
              {t("act.getFreeQuote")}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <button
              onClick={() => {
                track("cta_click", { cta: "open_concierge", location: "hero" });
                openConcierge();
              }}
              data-testid="hero-concierge-button"
              className="inline-flex items-center justify-center gap-3 border border-border text-foreground font-mono-tech text-xs tracking-widest uppercase px-8 h-14 hover:border-primary hover:text-primary transition-colors backdrop-blur-sm"
            >
              {t("hero.instant")}
            </button>
          </Reveal>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted-foreground">
          <span className="font-mono-tech text-[10px] tracking-widest uppercase">{t("hero.choosePath")}</span>
          <ArrowDown size={18} className="animate-bounce text-primary" />
        </div>
      </section>

      <Marquee
        items={[
          "Custom Homes",
          "Renovations",
          "Commercial Build-Outs",
          "Branding",
          "Web Design",
          "Online Marketing",
          "AI & Data",
          "Licensed & Insured",
        ]}
      />

      {/* CHOOSE YOUR PATH HUB */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-20 pb-10">
        <SectionHeading
          overline="The Show / Choose your path"
          title="One roof. Two disciplines."
          intro="Most firms build OR market — then leave you to bridge the gap. We do both, under one accountable team. Tell us where you're headed and we'll route you to exactly what moves the needle."
          index="hub"
        />
      </div>
      <ChoosePathHub />

      {/* WHY DIFFERENT */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeading
            overline="Why MRE is different"
            title="A builder fluent in tech."
            intro="The owner is building toward the future of construction and technology. This site is proof: the same team that frames your walls can frame your brand."
          />
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-2 font-mono-tech text-xs tracking-widest uppercase text-primary hover:gap-3 transition-all"
            data-testid="why-about-link"
          >
            Our story <ArrowRight size={14} />
          </Link>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 border-t border-l border-border">
          {[
            { t: "One partner", d: "Structure and signal under one roof. Fewer handoffs, one accountable team." },
            { t: "Honest pricing", d: "Premium feel, fair price. Transparent quotes — never cheap, never surprises." },
            { t: "Process clarity", d: "Four clear steps from first call to handover. You always know what's next." },
            { t: "Tech-forward", d: "AI tools, dashboards and modern web baked in — not bolted on after." },
          ].map((it, i) => (
            <Reveal key={it.t} delay={i * 80} className="border-b border-r border-border p-8 hover:bg-secondary/40 transition-colors">
              <span className="font-mono-tech text-xs tracking-widest text-primary">0{i + 1}</span>
              <h3 className="font-display uppercase tracking-tight text-2xl mt-3">{it.t}</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">{it.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SCROLL BUILD SEQUENCE */}
      <ScrollBuildSequence />

      {/* STATS */}
      <section className="border-b border-border" data-testid="stats-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s) => (
            <StatCounter key={s.label} {...s} />
          ))}
        </div>
      </section>

      {/* FEATURED WORK — construction + brand side by side */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <SectionHeading
            overline="Featured work"
            title="Built & launched."
            intro="Real homes, spaces and brands — delivered by one team. Picture your project finished, on time, and open for business."
          />
          <Link
            to="/services"
            className="inline-flex items-center gap-2 font-mono-tech text-xs tracking-widest uppercase text-primary hover:gap-3 transition-all"
            data-testid="featured-all-link"
          >
            All services <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          {featured.map((p, i) => (
            <Link
              key={p.id}
              to={`/services/${p.service}`}
              className="group relative bg-background overflow-hidden"
              data-testid={`featured-project-${p.id}`}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={p.image}
                  alt={`${p.title} — ${p.category} in ${p.location}`}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/10 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-5">
                <span className={`font-mono-tech text-[10px] tracking-widest uppercase ${p.discipline === "Digital" ? "text-primary" : "text-muted-foreground"}`}>
                  {p.discipline} / {p.category}
                </span>
                <h3 className="font-display uppercase tracking-tight text-xl mt-1 leading-tight">{p.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-4">
          <SectionHeading
            overline="Renovation / Before · After"
            title="Drag to reveal the build."
            intro="See the change in a real MRE renovation. Slide to compare the before and after photographs."
          />
        </div>
        <div className="lg:col-span-8">
          <BeforeAfterSlider
            before={IMAGES.reno_before}
            after={IMAGES.reno_after}
            beforeLabel="Before"
            afterLabel="After"
            testId="home-before-after"
          />
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-y border-border bg-secondary/20" data-testid="process-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <SectionHeading
            overline="The process"
            title="Four steps. No surprises."
            intro="Clear scope. Transparent pricing. Weekly updates. From the first call to the keys in your hand, you'll always know exactly what's next — and what it costs."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 mt-12 border-t border-l border-border">
            {PROCESS.map((p, i) => (
              <Reveal key={p.no} delay={i * 80} className="border-b border-r border-border p-8">
                <span className="font-display text-5xl text-primary">{p.no}</span>
                <h3 className="font-display uppercase tracking-tight text-2xl mt-4">{p.title}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <SectionHeading overline="FAQ" title="Good questions." />
        </div>
        <div className="lg:col-span-8 border-t border-border">
          {FAQS.map((f, i) => (
            <Reveal key={i} className="border-b border-border py-6">
              <h3 className="font-display uppercase tracking-tight text-xl text-foreground">{f.q}</h3>
              <p className="mt-2 text-muted-foreground leading-relaxed max-w-2xl">{f.a}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
